/**
 * CommonFloodgate.ts
 *
 * Common functionalities shared by App_Floodgate and App_Floodgate_Bare modules.
 */

import * as Configuration from "./Configuration/Configuration";
import * as GovernanceServiceClient from "./GovernanceService/GovernanceServiceClient";
import * as PersonalizerClient from "./Personalizer/PersonalizerClient";
import * as Window from "./Window/Window";

import { Api, GovernedChannelType, IFloodgateStorageProvider } from "@ms-ofb/floodgate-shared-typings";
import { FloodgateEngine } from "@ms-ofb/officefloodgatecore";
import { DynamicCampaignDefinitionProvider, FilterValidCampaignDefinitions } from "@ms-ofb/officefloodgatecore/dist/src/Campaign/CampaignDefinitionProvider";
import { TelemetryEvent } from "@ms-ofb/officefloodgatecore/dist/src/Constants";
import { setFeatureGateProvider as setFloodgateCoreFGProvider } from "@ms-ofb/officefloodgatecore/dist/src/FeatureGateProvider";
import { Governor } from "@ms-ofb/officefloodgatecore/dist/src/Governor";
import SurveyActivityListener from "@ms-ofb/officefloodgatecore/dist/src/SurveyActivityListener";
import { IFloodgateEngineConfig } from "@ms-ofb/officefloodgatecore/src/Api/IFloodgateEngineConfig";
import { SettingKey, getDynamicSetting } from "@ms-ofb/officepersonalization/dist/lib/Configuration/DynamicConfiguration";

import { TelemetryLogger } from "@ms-ofb/officefloodgatecore";
import AdaptiveSurveyLauncherFactory from "./FloodgateCore/AdaptiveSurveyLauncherFactory";
import FloodgateEnvironmentProvider from "./FloodgateCore/FloodgateEnvironmentProvider";
import FloodgateStorageProvider from "./FloodgateCore/FloodgateStorageProvider";
import FloodgateStringProvider from "./FloodgateCore/FloodgateStringProvider";
import TransporterFactory from "./FloodgateCore/TransporterFactory";
import { TargetedMessagingCampaignDefinitionProvider } from "./TargetedMessaging/TargetedMessagingCampaignDefinitionProvider";
import { UserFactsProvider } from "./TargetedMessaging/UserFactsProvider";
import { CentroThemeDataSource } from "./UI/DynamicUx/CentroThemeDataSource";

/**
 * Has floodgate been initialized
 */
let initialized = false;
let startCount = 0;

let dynamicCampaignDataProvider: DynamicCampaignDefinitionProvider<Api.EcsResponseBase>;
let engine: FloodgateEngine;
let engineStartPromise: Promise<any> = null;
let engineTmsProvider: TargetedMessagingCampaignDefinitionProvider = null;
let userFactsProvider: UserFactsProvider = null;
let stringProvider: Api.IFloodgateStringProvider;
let storageProvider: Api.IFloodgateStorageProvider;

export function initialize(): void {
	if (!FloodgateStorageProvider.isStorageAvailable()) {
		throw new Error("window.localStorage is not available.");
	}

	Window.get().OfficeBrowserFeedback.floodgate = Window.get().OfficeBrowserFeedback.floodgate || {} as any;

	let floodgateInitOptions = Window.get().OfficeBrowserFeedback.floodgate.initOptions;
	floodgateInitOptions = floodgateInitOptions || {} as any;

	// Validate and copy campaign definitions to local storage
	if (floodgateInitOptions.campaignDefinitions !== undefined) {
		const filteredResult = FilterValidCampaignDefinitions(floodgateInitOptions.campaignDefinitions);

		if (filteredResult.error) {
			throw new Error("floodgate.initOptions.campaignDefinitions error: " + filteredResult.error);
		}

		(new FloodgateStorageProvider()).write(
			Api.FloodgateStorageProviderFileType.CampaignDefinitions,
			JSON.stringify(floodgateInitOptions.campaignDefinitions)
		);
	}

	const configuration = Configuration.get();

	configuration.setFloodgateInitOptions(floodgateInitOptions);
	SurveyActivityListener.resetSessionActivity();
	PersonalizerClient.get().setup(configuration);
	GovernanceServiceClient.get().setup(configuration);
	setFloodgateCoreFGProvider(Configuration.get().getFeatureGateProvider());
	setInitialized(true);

	if (!configuration.getFloodgateInitOptions().surveyEnabled) {
		const telemetryLogger = TelemetryLogger.getTelemetryLogger();
		if (telemetryLogger) {
			telemetryLogger.log_Event(TelemetryEvent.Floodgate.Start.SurveysDisabled,
				{ Count: startCount, Message: "Floodgate initialized with surveys disabled (surveyEnabled is false)" });
		}
	}

	if (!configuration.getFloodgateInitOptions().messagingEnabled) {
		const telemetryLogger = TelemetryLogger.getTelemetryLogger();
		if (telemetryLogger) {
			telemetryLogger.log_Event(TelemetryEvent.Floodgate.Start.MessagingDisabled,
				{ Count: startCount, Message: "Floodgate initialized with messaging disabled (messagingEnabled is false)" });
		}
	}
}

export function setInitialized(initValue: boolean): void {
	initialized = initValue;
}

export function getInitialized(): boolean {
	return initialized;
}

export function setEngineStartPromise(startPromise: Promise<any>): any {
	engineStartPromise = startPromise;
}

export function getEngineStartPromise(): Promise<any> {
	return engineStartPromise;
}

export function setStringProvider(provider: Api.IFloodgateStringProvider): void {
	stringProvider = provider;
}

/**
 * To support the multi-window scenario on web we need to initialize the engine on each resume.
 * Hence this separate method which does make() and start() together.
 */
export function startInternal(loadSynchronously: boolean, customEngine: FloodgateEngine = null): Promise<any> {

	startCount++;

	const rejectAndLogError = (reject: (reason?: any) => void, errorMessage: string, error?: Error, internalSampleRate?: number) => {
		const telemetryLogger = TelemetryLogger.getTelemetryLogger();
		if (telemetryLogger) {
			telemetryLogger.log_Event(TelemetryEvent.Floodgate.Start.Failed,
				{ Count: startCount, ErrorMessage: errorMessage, ErrorDetails: error && error.stack },
				internalSampleRate);
		}

		reject(errorMessage);
	};

	if (!initialized) {
		return new Promise((resolve, reject) => {
			rejectAndLogError(reject, "CommonFloodgate is not initialized.", undefined /* error */, 0.01 /* internalSampleRate */);
		});
	}

	if (engineStartPromise) {
		return engineStartPromise;
	}

	const startPromise = new Promise((resolve, reject) => {
		try {
			const commonInitOptions = Configuration.get().getCommonInitOptions();
			const campaignDefinitionProviders: Api.ICampaignDefinitionProvider[] = [];

			if (!commonInitOptions) {
				rejectAndLogError(reject, "Invalid floodgate common init options, state: " + initialized);
				return;
			}

			let floodgateInitOptions = Configuration.get().getFloodgateInitOptions();
			const telemetryLogger = TelemetryLogger.getTelemetryLogger();
			const logTelemetryEvent = (eventName: string, properties: Api.ITelemetryProperties, internalSampleRate?: number) => {
				if (telemetryLogger) {
					telemetryLogger.log_Event(eventName,
						properties, internalSampleRate);
				}
			};
			if (!floodgateInitOptions) {
				logTelemetryEvent(TelemetryEvent.Floodgate.Start.Warning,
					{ Count: startCount, ErrorMessage: "Invalid floodgate init options, state: " + initialized });

				Configuration.get().setFloodgateInitOptions({} as any);
				floodgateInitOptions = Configuration.get().getFloodgateInitOptions();
			}

			if (!customEngine && !engineTmsProvider) {
				// AssetsProvider wraps auth callbacks which should not run every time we start()
				const authTokenCallback = floodgateInitOptions && floodgateInitOptions.authTokenCallback;
				if (authTokenCallback) {
					engineTmsProvider = new TargetedMessagingCampaignDefinitionProvider();
					userFactsProvider = new UserFactsProvider();
				}
			}

			if (engineTmsProvider) {
				campaignDefinitionProviders.push(engineTmsProvider);
			}

			if (floodgateInitOptions?.dynamicCampaignConfig?.enabled) {
				if (floodgateInitOptions?.dynamicCampaignConfig?.ecsClient) {
					logTelemetryEvent(TelemetryEvent.Floodgate.Start.DynamicCampaignEnabled,
						{ Count: startCount }, 0.01);

					if (!dynamicCampaignDataProvider) {
						dynamicCampaignDataProvider = new DynamicCampaignDefinitionProvider(floodgateInitOptions.dynamicCampaignConfig.ecsClient);
					}
					campaignDefinitionProviders.push(dynamicCampaignDataProvider);
				} else {
					logTelemetryEvent(TelemetryEvent.Floodgate.Start.DynamicCampaignClientUndefined,
						{ Count: startCount }, 0.1);
				}
			}

			const currentTeachingCooldown = Governor.GetChannelCoolDown(GovernedChannelType.TeachingMessage);
			const teachingCooldown = getDynamicSetting(SettingKey.teachingMessageCooldown, currentTeachingCooldown);
			Governor.SetDefaultChannelCoolDown(GovernedChannelType.TeachingMessage, teachingCooldown);

			const floodgateEngineConfig: IFloodgateEngineConfig = {
				shouldFetchSurveyCdnConcurrently: floodgateInitOptions?.dynamicUxConfig?.cdnStringConfig.fetchCdnConcurrently,
				uiStringsCdnEnabled: floodgateInitOptions?.dynamicUxConfig?.cdnStringConfig.uiStringsCdnEnabled,
				uiStringsLoadingTimeout: floodgateInitOptions?.dynamicUxConfig?.cdnStringConfig.uiStringsLoadingTimeout,
				uiStringCdnRetryLimit: floodgateInitOptions?.dynamicUxConfig?.cdnStringConfig.uiStringCdnRetryLimit,
				locale: commonInitOptions.originalLocale
			}

			engine = customEngine || FloodgateEngine.make(
				commonInitOptions.build || "",
				AdaptiveSurveyLauncherFactory.make(),
				floodgateInitOptions.onSurveyActivatedCallback,
				getStorageProvider(),
				floodgateInitOptions.settingStorageCallback,
				stringProvider ? stringProvider : new FloodgateStringProvider(),
				new FloodgateEnvironmentProvider(),
				new TransporterFactory(),
				campaignDefinitionProviders,
				userFactsProvider,
				GovernanceServiceClient.get(),
				floodgateInitOptions.isPriorityEnabled,
				floodgateEngineConfig,
				floodgateInitOptions.appGovernanceProvider,
				floodgateInitOptions.logExperimentTrigger,
				floodgateInitOptions.campaignTelemetrySamplingConfig,
				PersonalizerClient.get(),
				floodgateInitOptions.messagingEnabled,
				floodgateInitOptions.surveyEnabled,
				engine && engine.getSurveyClient()
			);

			if (!engine) {
				setEngineStartPromise(null);
				rejectAndLogError(reject, "Failed to create floodgate engine");
				return;
			}

			// start subsequent invocations in sync mode
			const shouldStartEngineInSync = loadSynchronously || startCount > 1;
			if (shouldStartEngineInSync) {
				engine.start();
				resolve(null);
			} else {
				const enginePromise = engine.startAsync();
				if (!enginePromise) {
					// ideally this should never happen
					setEngineStartPromise(null);
					rejectAndLogError(reject, "Failed to get floodgate engine");
					return;
				}

				enginePromise.then(
					function onFulfilled() {
						resolve(null);
					}
				).catch(
					function onRejected(startError: Error) {
						setEngineStartPromise(null);
						rejectAndLogError(reject, "Failed to start floodgate engine", startError);
					}
				);
			}
		} catch (e) {
			setEngineStartPromise(null);
			rejectAndLogError(reject, "Failed to load floodgate engine: " + (e && e.toString()), e);
		}
	});

	setEngineStartPromise(startPromise);

	// reset on error
	startPromise.catch((error) => {
		setEngineStartPromise(null);
	});

	return startPromise;
}

export function start(loadSynchronously = false): Promise<any> {
	return startInternal(loadSynchronously);
}

/**
 * A proxy for the engine.stop() method for consistency with the start() method.
 */
export function stop() {
	if (engine) {
		engine.stop();
	}

	setEngineStartPromise(null);
}

/**
 * Get the engine object
 * @returns the engine object
 */
export function getEngine(): FloodgateEngine {
	return engine;
}

/**
 * Reset the floodgate engine state. Used in unit tests.
 */
export function resetEngine() {
	startCount = 0;
	engineStartPromise = null;
	engine = null;
	engineTmsProvider = null;
	userFactsProvider = null;
}

/**
 * Reset the floodgate module. Used in unit tests.
 */
export function reset() {
	initialized = false;
	resetEngine();
}

export function getStorageProvider(): IFloodgateStorageProvider {
	if (!storageProvider) {
		storageProvider = new FloodgateStorageProvider();
	}
	return storageProvider;
}

export function updateThemeInternal(themeConfig: Configuration.IDynamicUxThemeConfig): Promise<any> {
	try {
		Configuration.get().updateTheme(themeConfig, CentroThemeDataSource.get());
		return Promise.resolve();
	} catch (error) {
		return Promise.reject(error);
	}
}

Window.setFloodgateGetEngine(getEngine);
Window.setFloodgateStart(start);
Window.setFloodgateStop(stop);
Window.setStorageProvider(getStorageProvider);
Window.setFloodgateUpdateTheme(updateThemeInternal);
